import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { getIdToken, auth } from './firebase';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:4000';

// Extend axios config to include retry flag
declare module 'axios' {
  export interface InternalAxiosRequestConfig {
    _retry?: boolean;
  }
}

// Create axios instance
export const api = axios.create({
  baseURL: API_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Flag to prevent multiple refresh attempts
let isRefreshing = false;

// Request interceptor to add auth token
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    try {
      const token = await getIdToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    } catch {
      // Token retrieval failed, continue without auth
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      // Mark this request as retried to prevent infinite loops
      originalRequest._retry = true;
      
      if (!isRefreshing) {
        isRefreshing = true;
        
        try {
          // Try to get a fresh token (Firebase will refresh if needed)
          const user = auth.currentUser;
          if (user) {
            const newToken = await user.getIdToken(true); // Force refresh
            if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              isRefreshing = false;
              return api(originalRequest);
            }
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        }
        
        isRefreshing = false;
      }
      
      // If we can't refresh the token and user is not authenticated, redirect to login
      if (!auth.currentUser) {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// API response type
export interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
  meta?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

// Google registration response type
export interface GoogleRegistrationRequiredData {
  firebaseUid: string;
  email: string;
  firstName: string;
  lastName: string;
  avatarUrl: string | null;
  provider: string;
}

export interface GoogleLoginResponse {
  success: boolean;
  data?: { user: unknown } | GoogleRegistrationRequiredData;
  code?: string;
  message?: string;
  error?: string;
}

// Auth API
export const authApi = {
  register: (data: {
    email: string;
    firstName: string;
    lastName: string;
    role: string;
    idToken: string;
  }) => api.post<ApiResponse<{ user: unknown }>>('/api/auth/register', data),

  login: (idToken: string) =>
    api.post<GoogleLoginResponse>('/api/auth/login', { idToken }),

  /**
   * Complete Google registration with role selection
   * Called when a new Google user needs to select their role
   */
  completeGoogleRegistration: (data: {
    idToken: string;
    role: 'STUDENT' | 'PROFESSOR';
    firstName?: string;
    lastName?: string;
  }) => api.post<ApiResponse<{ user: unknown }> & { code?: string }>('/api/auth/google/complete', data),

  /**
   * Switch user role (for existing users who want to change from STUDENT to PROFESSOR)
   * Only allows STUDENT -> PROFESSOR switch
   */
  switchRole: (data: {
    idToken: string;
    newRole: 'STUDENT' | 'PROFESSOR';
  }) => api.post<ApiResponse<{ user: unknown }>>('/api/auth/switch-role', data),

  logout: () => api.post('/api/auth/logout'),

  me: () => api.get<ApiResponse<unknown>>('/api/auth/me'),

  refresh: (idToken: string) =>
    api.post('/api/auth/refresh', { idToken }),
};

// Account deletion check response
export interface DeletionCheckResponse {
  canDelete: boolean;
  pendingBalance?: number;
  reason?: string;
  // Student-specific fields
  hasActiveEnrollments?: boolean;
  hasDocuments?: boolean;
  hasOngoingCourses?: boolean;
  enrolledCoursesCount?: number;
  documentsCount?: number;
  upcomingSessionsCount?: number;
  enrolledCourses?: Array<{
    id: string;
    title: string;
    courseType: 'RECORDED' | 'LIVE';
    progress: number;
  }>;
  documentsByCourse?: Record<string, {
    courseTitle: string;
    documents: Array<{
      id: string;
      title: string;
      fileName: string;
      fileUrl: string;
      fileSize: number;
    }>;
  }>;
  upcomingSessions?: Array<{
    id: string;
    title: string;
    scheduledAt: string;
    courseId: string;
    courseTitle: string;
  }>;
  warnings?: string[];
}

// Users API
export const usersApi = {
  getProfile: () => api.get<ApiResponse<unknown>>('/api/users/profile'),

  updateProfile: (data: {
    firstName?: string;
    lastName?: string;
    bio?: string;
    avatarUrl?: string;
  }) => api.patch<ApiResponse<unknown>>('/api/users/profile', data),

  getStats: () => api.get<ApiResponse<unknown>>('/api/users/stats'),

  getUser: (id: string) => api.get<ApiResponse<unknown>>(`/api/users/${id}`),

  /**
   * Check if the user's account can be deleted
   * Professors with pending balance cannot delete their account
   */
  checkDeletion: () => api.get<ApiResponse<DeletionCheckResponse>>('/api/users/check-deletion'),

  /**
   * Permanently delete the user's account and all associated data
   */
  deleteAccount: () => api.delete<ApiResponse<{ message: string }>>('/api/users/account'),
};

// Course deletion check response
export interface CourseDeletionCheckResponse {
  canDelete: boolean;
  courseId: string;
  courseTitle: string;
  courseType: 'RECORDED' | 'LIVE';
  status: string;
  price: number;
  isPaidCourse: boolean;
  hasActiveEnrollments: boolean;
  hasUpcomingMeetings: boolean;
  hasPaidEnrollments: boolean;
  requiresSupport: boolean;
  canNotifyStudents: boolean;
  activeEnrollmentsCount: number;
  paidEnrollmentsCount: number;
  upcomingMeetingsCount: number;
  documentsCount: number;
  upcomingMeetings: Array<{
    id: string;
    title: string;
    scheduledAt: string;
  }>;
  enrolledStudents: Array<{
    id: string;
    studentId: string;
    studentName: string;
    avatarUrl?: string;
    enrolledAt: string;
    progress: number;
  }>;
  warnings: string[];
  blockedReason?: string;
}

// Courses API
export const coursesApi = {
  getAll: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    categoryId?: string;
    level?: string;
    minPrice?: number;
    maxPrice?: number;
  }) => api.get<ApiResponse<unknown[]>>('/api/courses', { params }),

  /**
   * Browse courses with enrollment status (protected endpoint)
   * Use this for authenticated students to see which courses they're enrolled in
   */
  browse: (params?: {
    page?: number;
    limit?: number;
    search?: string;
    categoryId?: string;
    level?: string;
    minPrice?: number;
    maxPrice?: number;
  }) => api.get<ApiResponse<unknown[]>>('/api/courses/browse', { params }),

  getFeatured: (limit?: number) => api.get<ApiResponse<unknown[]>>('/api/courses/featured', { params: { limit } }),

  getCategories: () => api.get<ApiResponse<unknown[]>>('/api/courses/categories'),

  getMyCourses: () => api.get<ApiResponse<unknown[]>>('/api/courses/my'),

  getById: (id: string, includeContent?: boolean) =>
    api.get<ApiResponse<unknown>>(`/api/courses/${id}`, {
      params: { includeContent },
    }),

  create: (data: unknown) =>
    api.post<ApiResponse<unknown>>('/api/courses', data),

  update: (id: string, data: unknown) =>
    api.patch<ApiResponse<unknown>>(`/api/courses/${id}`, data),

  /**
   * Check if a course can be deleted/archived
   * Returns info about active enrollments and upcoming meetings
   */
  checkDeletion: (id: string) =>
    api.get<ApiResponse<CourseDeletionCheckResponse>>(`/api/courses/${id}/check-deletion`),

  /**
   * Send notification to enrolled students asking them to unenroll
   * Only works for courses without paid enrollments
   */
  notifyUnenroll: (id: string, message?: string) =>
    api.post<ApiResponse<{ notificationsSent: number; message: string }>>(`/api/courses/${id}/notify-unenroll`, { message }),

  /**
   * Delete (archive) a course
   * @param force - If true, forces deletion even with active enrollments (admin only)
   */
  delete: (id: string, force?: boolean) =>
    api.delete<ApiResponse<unknown>>(`/api/courses/${id}`, { params: { force } }),

  getReviews: (id: string, params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<unknown[]>>(`/api/courses/${id}/reviews`, { params }),

  getStats: (id: string) =>
    api.get<ApiResponse<unknown>>(`/api/courses/${id}/stats`),
};

// Enrollment response types
export interface EnrollmentData {
  id: string;
  userId: string;
  courseId: string;
  status: 'ACTIVE' | 'COMPLETED' | 'CANCELLED';
  progress: number;
  enrolledAt: string;
  completedAt?: string;
  lastAccessedAt?: string;
  course: {
    id: string;
    title: string;
    slug: string;
    thumbnailUrl?: string;
    totalLessons: number;
    totalDuration?: number;
    level?: string;
    price: number;
    courseType: 'RECORDED' | 'LIVE';
    professor: {
      id: string;
      firstName: string;
      lastName: string;
      avatarUrl?: string;
    };
  };
  lessonProgress?: Array<{
    lessonId: string;
    isCompleted: boolean;
    watchTime: number;
  }>;
}

export interface EnrollmentCheckResponse {
  isEnrolled: boolean;
  enrollment?: EnrollmentData;
}

// Enrollments API
export const enrollmentsApi = {
  getAll: (params?: { status?: string }) =>
    api.get<ApiResponse<EnrollmentData[]>>('/api/enrollments', { params }),

  getById: (id: string) =>
    api.get<ApiResponse<EnrollmentData>>(`/api/enrollments/${id}`),

  check: (courseId: string) =>
    api.get<ApiResponse<EnrollmentCheckResponse>>(`/api/enrollments/check/${courseId}`),

  enroll: (courseId: string) =>
    api.post<ApiResponse<EnrollmentData>>(`/api/enrollments/enroll/${courseId}`),

  cancel: (enrollmentId: string) =>
    api.post<ApiResponse<{ enrollmentId: string; cancelledAt: string }>>(`/api/enrollments/${enrollmentId}/cancel`),

  getProgress: (id: string) =>
    api.get<ApiResponse<unknown>>(`/api/enrollments/${id}/progress`),

  updateProgress: (id: string, data: { lessonId: string; isCompleted: boolean; watchTime?: number }) =>
    api.post<ApiResponse<unknown>>(`/api/enrollments/${id}/progress`, data),
};

// Payments API
export const paymentsApi = {
  createCheckout: (data: {
    courseId: string;
    successUrl: string;
    cancelUrl: string;
  }) => api.post<ApiResponse<{ sessionId: string; url: string }>>('/api/payments/checkout', data),

  getHistory: (params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<unknown[]>>('/api/payments/history', { params }),

  getEarnings: (params?: { period?: string }) =>
    api.get<ApiResponse<unknown>>('/api/payments/earnings', { params }),

  /** Get detailed earnings with course breakdown */
  getDetailedEarnings: () =>
    api.get<ApiResponse<DetailedEarningsData>>('/api/payments/earnings/detailed'),

  connectOnboard: (data: { returnUrl: string; refreshUrl: string }) =>
    api.post<ApiResponse<{ onboardingUrl: string }>>('/api/payments/connect/onboard', data),

  getConnectStatus: () =>
    api.get<ApiResponse<{
      hasAccount: boolean;
      isOnboarded: boolean;
      payoutsEnabled?: boolean;
      chargesEnabled?: boolean;
    }>>('/api/payments/connect/status'),

  /** Get Stripe Express dashboard link for managing payouts */
  getDashboardLink: () =>
    api.post<ApiResponse<{ url: string }>>('/api/payments/connect/dashboard'),
};

// Detailed Earnings Types
export interface EarningsOverview {
  lifetimeEarnings: number;
  lifetimeRevenue: number;
  lifetimeSales: number;
  currentMonthEarnings: number;
  currentMonthSales: number;
  previousMonthEarnings: number;
  previousMonthSales: number;
  monthlyGrowth: number;
}

export interface CourseEarningBreakdown {
  id: string;
  title: string;
  slug: string;
  thumbnailUrl?: string;
  price: number;
  status: string;
  rating: number | null;
  totalReviews: number;
  totalEnrollments: number;
  totalSales: number;
  totalEarnings: number;
  currentMonthEarnings: number;
  createdAt: string;
}

export interface EarningsTransaction {
  id: string;
  amount: number;
  createdAt: string;
  student: {
    id: string;
    name: string;
    avatarUrl?: string;
  };
  course: {
    id: string;
    title: string;
    slug: string;
    thumbnailUrl?: string;
  };
}

export interface MonthlyEarningsTrend {
  month: string;
  earnings: number;
  sales: number;
}

export interface TopPerformingCourse {
  id: string;
  title: string;
  thumbnailUrl?: string;
  rating: number | null;
  enrollments: number;
  totalEarnings: number;
}

export interface DetailedEarningsData {
  overview: EarningsOverview;
  courseBreakdown: CourseEarningBreakdown[];
  recentTransactions: EarningsTransaction[];
  monthlyTrend: MonthlyEarningsTrend[];
  topPerformers: TopPerformingCourse[];
}

// Meeting types
export interface MeetingData {
  id: string;
  title: string;
  description?: string;
  scheduledAt: string;
  duration: number;
  status: 'SCHEDULED' | 'LIVE' | 'ENDED' | 'CANCELLED';
  meetingType: 'SESSION' | 'QA_FINALE';
  sessionNumber?: number;
  jitsiRoomId: string;
  recordingUrl?: string;
  courseId: string;
  professorId: string;
  course: {
    id: string;
    title: string;
    slug?: string;
    thumbnailUrl?: string;
    courseType: 'RECORDED' | 'LIVE';
  };
  professor: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
  };
}

export interface SessionStats {
  courseId: string;
  courseType: 'RECORDED' | 'LIVE';
  totalSessions: number;
  scheduledSessions: number;
  completedSessions: number;
  liveSessions: number;
  remainingSlots: number;
  enrolledStudents: number;
  hasEnrollments: boolean;
  canScheduleMore: boolean;
}

// Meetings API
export const meetingsApi = {
  getUpcoming: (params?: { limit?: number }) =>
    api.get<ApiResponse<MeetingData[]>>('/api/meetings/upcoming', { params }),

  getByCourse: (courseId: string) =>
    api.get<ApiResponse<MeetingData[]>>(`/api/meetings/course/${courseId}`),

  getSessionStats: (courseId: string) =>
    api.get<ApiResponse<SessionStats>>(`/api/meetings/course/${courseId}/stats`),

  getById: (id: string) =>
    api.get<ApiResponse<MeetingData>>(`/api/meetings/${id}`),

  create: (data: {
    courseId: string;
    title: string;
    description?: string;
    scheduledAt: string;
    duration: number;
    meetingType?: 'SESSION' | 'QA_FINALE';
    sessionNumber?: number;
  }) => api.post<ApiResponse<MeetingData>>('/api/meetings', data),

  update: (id: string, data: {
    title?: string;
    description?: string;
    scheduledAt?: string;
    duration?: number;
    sessionNumber?: number;
  }) =>
    api.patch<ApiResponse<MeetingData>>(`/api/meetings/${id}`, data),

  start: (id: string) =>
    api.post<ApiResponse<MeetingData>>(`/api/meetings/${id}/start`),

  end: (id: string, recordingUrl?: string) =>
    api.post<ApiResponse<MeetingData>>(`/api/meetings/${id}/end`, { recordingUrl }),

  cancel: (id: string) =>
    api.delete<ApiResponse<MeetingData>>(`/api/meetings/${id}`),

  // Get JWT token for joining a meeting (JaaS authentication)
  getJoinToken: (id: string) =>
    api.post<ApiResponse<{
      token?: string;
      meetingUrl: string;
      expiresIn?: number;
      isModerator: boolean;
      usePublicJitsi?: boolean;
      meeting?: {
        id: string;
        title: string;
        jitsiRoomId: string;
      };
    }>>(`/api/meetings/${id}/join-token`),
};

// Notifications API
export const notificationsApi = {
  getAll: (params?: { page?: number; limit?: number; unreadOnly?: boolean }) =>
    api.get<ApiResponse<unknown[]>>('/api/notifications', { params }),

  getUnreadCount: () =>
    api.get<ApiResponse<{ count: number }>>('/api/notifications/unread-count'),

  markAsRead: (id: string) =>
    api.patch<ApiResponse<unknown>>(`/api/notifications/${id}/read`),

  markAllAsRead: () =>
    api.patch<ApiResponse<unknown>>('/api/notifications/read-all'),

  delete: (id: string) =>
    api.delete<ApiResponse<unknown>>(`/api/notifications/${id}`),

  clear: (readOnly?: boolean) =>
    api.delete<ApiResponse<unknown>>('/api/notifications/clear', {
      params: { readOnly },
    }),
};

// Reviews API
export const reviewsApi = {
  create: (courseId: string, data: { rating: number; comment?: string }) =>
    api.post<ApiResponse<unknown>>('/api/courses/' + courseId + '/reviews', { ...data, courseId }),
};

// Document types
export type DocumentCategory = 
  | 'SYLLABUS'
  | 'LECTURE_NOTES'
  | 'RESOURCE'
  | 'ASSIGNMENT'
  | 'SOLUTION'
  | 'TEMPLATE'
  | 'OTHER';

export interface CourseDocument {
  id: string;
  title: string;
  description?: string;
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
  category: DocumentCategory;
  order: number;
  isPublic: boolean;
  createdAt: string;
  updatedAt: string;
  courseId: string;
  uploadedById: string;
  uploadedBy?: {
    id: string;
    firstName: string;
    lastName: string;
    avatarUrl?: string;
  };
}

// Documents API
export const documentsApi = {
  getByCourse: (courseId: string, params?: { category?: string; page?: number; limit?: number }) =>
    api.get<ApiResponse<{ documents: CourseDocument[]; hasAccess: boolean }>>(`/api/documents/course/${courseId}`, { params }),

  getById: (id: string) =>
    api.get<ApiResponse<CourseDocument>>(`/api/documents/${id}`),

  /**
   * Upload a document using presigned URL flow
   * 1. Get presigned URL from backend
   * 2. Upload file directly to storage
   * 3. Confirm upload to update database
   */
  upload: async (courseId: string, file: File, data: {
    title: string;
    description?: string;
    category?: DocumentCategory;
    isPublic?: boolean;
  }, onProgress?: (progress: number) => void): Promise<ApiResponse<CourseDocument>> => {
    // Step 1: Get presigned URL
    const presignedResponse = await api.post<ApiResponse<{
      uploadUrl: string;
      fileKey: string;
      fileUrl: string;
      expiresIn: number;
      maxSize: number;
    }>>('/api/uploads/presigned-url', {
      courseId,
      fileName: file.name,
      mimeType: file.type,
      uploadType: 'document',
      title: data.title,
      description: data.description,
      category: data.category,
      isPublic: data.isPublic,
    });

    if (!presignedResponse.data.success || !presignedResponse.data.data) {
      throw new Error(presignedResponse.data.error || 'Failed to get upload URL');
    }

    const { uploadUrl, fileKey } = presignedResponse.data.data;

    // Step 2: Upload directly to storage
    await new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const progress = Math.round((event.loaded / event.total) * 100);
          onProgress(progress);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

      xhr.open('PUT', uploadUrl);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });

    // Step 3: Confirm upload
    const confirmResponse = await api.post<ApiResponse<CourseDocument>>('/api/uploads/confirm', {
      courseId,
      uploadType: 'document',
      fileKey,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
      title: data.title,
      description: data.description,
      category: data.category,
      isPublic: data.isPublic,
    });

    return confirmResponse.data;
  },

  createFromUrl: (courseId: string, data: {
    title: string;
    description?: string;
    fileUrl: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    category?: DocumentCategory;
    isPublic?: boolean;
  }) => api.post<ApiResponse<CourseDocument>>(`/api/documents/url/${courseId}`, data),

  update: (id: string, data: {
    title?: string;
    description?: string;
    category?: DocumentCategory;
    order?: number;
    isPublic?: boolean;
  }) => api.patch<ApiResponse<CourseDocument>>(`/api/documents/${id}`, data),

  delete: (id: string) =>
    api.delete<ApiResponse<{ fileUrl: string }>>(`/api/documents/${id}`),
};

// Upload types
export type UploadType = 'document' | 'thumbnail' | 'preview-video' | 'lesson-video';

export interface UploadResult {
  url: string;
  key: string;
  size: number;
  courseTitle?: string;
}

export interface PresignedUrlResponse {
  uploadUrl: string;
  fileKey: string;
  fileUrl: string;
  expiresIn: number;
  maxSize: number;
  courseTitle?: string;
}

/**
 * Helper function to upload a file using presigned URL
 */
async function uploadWithPresignedUrl(
  file: File,
  uploadUrl: string,
  onProgress?: (progress: number) => void
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable && onProgress) {
        const progress = Math.round((event.loaded / event.total) * 100);
        onProgress(progress);
      }
    });

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new Error(`Upload failed with status ${xhr.status}`));
      }
    });

    xhr.addEventListener('error', () => reject(new Error('Upload failed')));
    xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));

    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('Content-Type', file.type);
    xhr.send(file);
  });
}

// Uploads API - for thumbnails, videos, and other media
// Uses presigned URLs for secure direct-to-storage uploads
export const uploadsApi = {
  /**
   * Upload a thumbnail using presigned URL flow
   */
  uploadThumbnail: async (
    courseId: string, 
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<UploadResult>> => {
    // Get presigned URL
    const presignedResponse = await api.post<ApiResponse<PresignedUrlResponse>>('/api/uploads/presigned-url', {
      courseId,
      fileName: file.name,
      mimeType: file.type,
      uploadType: 'thumbnail',
    });

    if (!presignedResponse.data.success || !presignedResponse.data.data) {
      throw new Error(presignedResponse.data.error || 'Failed to get upload URL');
    }

    const { uploadUrl, fileKey, fileUrl } = presignedResponse.data.data;

    // Upload to storage
    await uploadWithPresignedUrl(file, uploadUrl, onProgress);

    // Confirm upload
    await api.post<ApiResponse<unknown>>('/api/uploads/confirm', {
      courseId,
      uploadType: 'thumbnail',
      fileKey,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
    });

    return {
      success: true,
      data: {
        url: fileUrl,
        key: fileKey,
        size: file.size,
      },
    };
  },

  /**
   * Set thumbnail URL directly (for external URLs)
   */
  setThumbnailUrl: (courseId: string, url: string) =>
    api.post<ApiResponse<unknown>>('/api/uploads/set-url', { 
      courseId, 
      uploadType: 'thumbnail', 
      url 
    }),

  /**
   * Upload preview video using presigned URL flow
   */
  uploadPreviewVideo: async (
    courseId: string, 
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<UploadResult>> => {
    const presignedResponse = await api.post<ApiResponse<PresignedUrlResponse>>('/api/uploads/presigned-url', {
      courseId,
      fileName: file.name,
      mimeType: file.type,
      uploadType: 'preview-video',
    });

    if (!presignedResponse.data.success || !presignedResponse.data.data) {
      throw new Error(presignedResponse.data.error || 'Failed to get upload URL');
    }

    const { uploadUrl, fileKey, fileUrl } = presignedResponse.data.data;

    await uploadWithPresignedUrl(file, uploadUrl, onProgress);

    await api.post<ApiResponse<unknown>>('/api/uploads/confirm', {
      courseId,
      uploadType: 'preview-video',
      fileKey,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
    });

    return {
      success: true,
      data: {
        url: fileUrl,
        key: fileKey,
        size: file.size,
      },
    };
  },

  /**
   * Set preview video URL directly (for external URLs like YouTube)
   */
  setPreviewVideoUrl: (courseId: string, url: string) =>
    api.post<ApiResponse<unknown>>('/api/uploads/set-url', { 
      courseId, 
      uploadType: 'preview-video', 
      url 
    }),

  /**
   * Upload lesson video using presigned URL flow
   */
  uploadLessonVideo: async (
    courseId: string, 
    sectionId: string, 
    lessonId: string, 
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<ApiResponse<UploadResult>> => {
    const presignedResponse = await api.post<ApiResponse<PresignedUrlResponse>>('/api/uploads/presigned-url', {
      courseId,
      sectionId,
      lessonId,
      fileName: file.name,
      mimeType: file.type,
      uploadType: 'lesson-video',
    });

    if (!presignedResponse.data.success || !presignedResponse.data.data) {
      throw new Error(presignedResponse.data.error || 'Failed to get upload URL');
    }

    const { uploadUrl, fileKey, fileUrl } = presignedResponse.data.data;

    await uploadWithPresignedUrl(file, uploadUrl, onProgress);

    await api.post<ApiResponse<unknown>>('/api/uploads/confirm', {
      courseId,
      sectionId,
      lessonId,
      uploadType: 'lesson-video',
      fileKey,
      fileName: file.name,
      fileSize: file.size,
      mimeType: file.type,
    });

    return {
      success: true,
      data: {
        url: fileUrl,
        key: fileKey,
        size: file.size,
      },
    };
  },

  /**
   * Set lesson video URL directly (for external URLs)
   */
  setLessonVideoUrl: (
    courseId: string, 
    sectionId: string, 
    lessonId: string, 
    url: string, 
    duration?: number
  ) =>
    api.post<ApiResponse<unknown>>('/api/uploads/set-url', { 
      courseId, 
      sectionId,
      lessonId,
      uploadType: 'lesson-video', 
      url,
      duration,
    }),

  /**
   * Get presigned URL for direct upload (generic)
   */
  getPresignedUrl: (data: {
    courseId: string;
    sectionId?: string;
    lessonId?: string;
    fileName: string;
    mimeType: string;
    uploadType: UploadType;
  }) => api.post<ApiResponse<PresignedUrlResponse>>('/api/uploads/presigned-url', data),

  /**
   * Confirm upload after direct upload to storage
   */
  confirmUpload: (data: {
    courseId: string;
    sectionId?: string;
    lessonId?: string;
    uploadType: UploadType;
    fileKey: string;
    fileName: string;
    fileSize: number;
    mimeType: string;
    duration?: number;
  }) => api.post<ApiResponse<unknown>>('/api/uploads/confirm', data),

  /**
   * Check storage health
   */
  checkHealth: () => api.get<ApiResponse<{ configured: boolean; provider: string; isPrivate: boolean }>>('/api/uploads/health'),

  /**
   * Get download URL for a file (resolves private storage URLs)
   * Call this before downloading a file to get the actual download URL
   */
  getDownloadUrl: (fileUrl: string, expiresIn?: number) =>
    api.post<ApiResponse<{ url: string; expiresIn: number | null; isPrivate: boolean }>>('/api/uploads/download-url', {
      fileUrl,
      expiresIn: expiresIn || 3600,
    }),

  /**
   * Helper to download a file (handles both public and private URLs)
   */
  downloadFile: async (fileUrl: string, fileName?: string): Promise<void> => {
    try {
      // Check if this is a private storage URL
      if (fileUrl.startsWith('storage://')) {
        const response = await api.post<ApiResponse<{ url: string }>>('/api/uploads/download-url', {
          fileUrl,
          expiresIn: 3600,
        });
        
        if (response.data.success && response.data.data?.url) {
          fileUrl = response.data.data.url;
        }
      }

      // Open the download URL
      const link = document.createElement('a');
      link.href = fileUrl;
      link.target = '_blank';
      if (fileName) {
        link.download = fileName;
      }
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Download failed:', error);
      throw new Error('Failed to download file');
    }
  },
};

// Activity API response types
export interface DailyActivity {
  date: string;
  day: string;
  minutes: number;
  hours: number;
}

export interface ActivityData {
  period: string;
  startDate: string;
  endDate: string;
  totalMinutes: number;
  totalHours: number;
  averageMinutesPerDay: number;
  dailyActivity: DailyActivity[];
}

export interface ActivitySummary {
  todayMinutes: number;
  weekMinutes: number;
  monthMinutes: number;
  allTimeMinutes: number;
  todayHours: number;
  weekHours: number;
  monthHours: number;
  allTimeHours: number;
  currentStreak: number;
}

// Activities API
export const activitiesApi = {
  get: (params?: { period?: 'week' | 'month' | 'year'; startDate?: string; endDate?: string }) =>
    api.get<ApiResponse<ActivityData>>('/api/activities', { params }),

  getSummary: () =>
    api.get<ApiResponse<ActivitySummary>>('/api/activities/summary'),

  record: (data: { minutes: number; courseId?: string }) =>
    api.post<ApiResponse<unknown>>('/api/activities', data),
};

// Support Tickets API Types
export interface TicketMessage {
  id: string;
  message: string;
  isStaff: boolean;
  createdAt: string;
}

export interface SupportTicket {
  id: string;
  zohoTicketId: string;
  zohoTicketNumber: string;
  subject: string;
  description: string;
  email: string;
  category: 'GENERAL' | 'BILLING' | 'TECHNICAL' | 'COURSE' | 'ACCOUNT' | 'OTHER';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'URGENT';
  status: 'OPEN' | 'IN_PROGRESS' | 'WAITING_RESPONSE' | 'RESOLVED' | 'CLOSED'; // IN_PROGRESS and WAITING_RESPONSE are legacy, displayed as OPEN
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
  userId?: string;
  messages?: TicketMessage[];
  _count?: {
    messages: number;
  };
}

export interface GuestTicketResponse {
  ticketNumber: string;
  message: string;
}

export interface TicketStats {
  total: number;
  open: number;
  inProgress: number;
  waitingResponse: number;
  resolved: number;
  closed: number;
}

// Support Tickets API
export const ticketsApi = {
  getAll: (params?: { status?: string; page?: number; limit?: number }) =>
    api.get<ApiResponse<{ tickets: SupportTicket[]; pagination: { page: number; limit: number; total: number; totalPages: number } }>>('/api/tickets', { params }),

  getById: (id: string) =>
    api.get<ApiResponse<SupportTicket>>(`/api/tickets/${id}`),

  getStats: () =>
    api.get<ApiResponse<TicketStats>>('/api/tickets/stats'),

  create: (data: {
    subject: string;
    description: string;
    category?: string;
    priority?: string;
  }) => api.post<ApiResponse<SupportTicket>>('/api/tickets', data),

  // Guest ticket creation (no auth required)
  createGuest: (data: {
    email: string;
    name: string;
    subject: string;
    description: string;
    category?: string;
  }) => api.post<ApiResponse<GuestTicketResponse>>('/api/tickets/guest', data),

  updateTicket: (ticketId: string, data: { description: string; category: string; priority: string }) =>
    api.post<ApiResponse<{ success: boolean; message: string }>>(`/api/tickets/${ticketId}/update`, data),

  close: (ticketId: string) =>
    api.post<ApiResponse<SupportTicket>>(`/api/tickets/${ticketId}/close`),
};

// ============ VERIFICATION API ============

export interface VerificationSession {
  sessionId: string;
  verificationUrl: string;
  expiresAt: string;
}

export interface VerificationStatus {
  verificationStatus: 'UNVERIFIED' | 'PENDING' | 'VERIFIED' | 'FAILED' | 'EXPIRED';
  verifiedAt?: string;
  isVerified: boolean;
  // Session info for resuming verification
  sessionId?: string;
  verificationUrl?: string;
}

export interface ResumeVerificationResponse {
  sessionId: string;
  verificationUrl: string;
  resumed: boolean;
  expiresAt?: string;
  alreadyVerified?: boolean;
  status?: string;
}

export const verificationApi = {
  getStatus: () =>
    api.get<ApiResponse<{ diditConfigured: boolean; requireLiveness: boolean; autoVerify: boolean }>>('/api/verification/status'),

  start: (data?: { redirectUrl?: string; failureUrl?: string }) =>
    api.post<ApiResponse<VerificationSession>>('/api/verification/start', data),

  check: () =>
    api.get<ApiResponse<VerificationStatus>>('/api/verification/check'),

  resume: (data?: { redirectUrl?: string; failureUrl?: string }) =>
    api.post<ApiResponse<ResumeVerificationResponse>>('/api/verification/resume', data),
};

// ============ MODERATION API ============

export interface ModerationResult {
  flagged: boolean;
  categories: Record<string, boolean>;
  categoryScores: Record<string, number>;
  maxScore: number;
  flaggedCategories: string[];
}

export interface CourseModerationResult {
  courseId: string;
  title: ModerationResult;
  description: ModerationResult;
  thumbnail?: ModerationResult & { analysis: string };
  overall: {
    flagged: boolean;
    maxScore: number;
    flaggedCategories: string[];
    recommendation: 'APPROVE' | 'REVIEW' | 'REJECT';
  };
}

export const moderationApi = {
  getStatus: () =>
    api.get<ApiResponse<{ openaiConfigured: boolean; imageModeration: boolean; threshold: number }>>('/api/moderation/status'),

  moderateText: (data: { text: string; contentType: string; courseId?: string }) =>
    api.post<ApiResponse<ModerationResult>>('/api/moderation/text', data),

  moderateCourse: (data: { courseId: string; title: string; description: string; thumbnailUrl?: string }) =>
    api.post<ApiResponse<CourseModerationResult>>('/api/moderation/course', data),
};

// ============ REPORTS API ============

export interface ContentReport {
  id: string;
  reportType: 'COURSE' | 'LIVE_SESSION' | 'PROFESSOR';
  reason: string;
  description?: string;
  evidenceUrls: string[];
  timestamp?: string;
  status: 'PENDING' | 'INVESTIGATING' | 'RESOLVED' | 'DISMISSED';
  priority: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  courseId?: string;
  meetingId?: string;
  reportedUserId?: string;
  resolution?: string;
  resolutionType?: string;
  createdAt: string;
  reporter?: { firstName: string; lastName: string };
  reportedUser?: { firstName: string; lastName: string };
  course?: { id: string; title: string };
  meeting?: { id: string; title: string };
}

export const reportsApi = {
  create: (data: {
    reportType: 'COURSE' | 'LIVE_SESSION' | 'PROFESSOR';
    reason: string;
    description?: string;
    courseId?: string;
    meetingId?: string;
    reportedUserId?: string;
    evidenceUrls?: string[];
    timestamp?: string;
  }) => api.post<ApiResponse<ContentReport>>('/api/reports', data),

  getMy: () =>
    api.get<ApiResponse<ContentReport[]>>('/api/reports/my'),
};

// ============ ADMIN API ============

export interface AdminStats {
  courses: {
    total: number;
    pendingReview: number;
    approved: number;
    rejected: number;
    published: number;
  };
  users: {
    total: number;
    students: number;
    professors: number;
    verifiedProfessors: number;
    suspendedProfessors: number;
  };
  reports: {
    total: number;
    pending: number;
    critical: number;
    today: number;
  };
  violations: {
    total: number;
    thisMonth: number;
  };
  recordings: {
    total: number;
    flagged: number;
  };
  enrollments: {
    total: number;
    thisWeek: number;
  };
  revenue: {
    totalRevenue: number;
    platformFees: number;
  };
}

export interface CourseForReview {
  id: string;
  title: string;
  slug: string;
  description: string;
  thumbnailUrl?: string;
  price: number;
  status: string;
  courseType: 'RECORDED' | 'LIVE';
  submittedForReviewAt: string;
  moderationScore?: number;
  moderationFlags: string[];
  professor: {
    id: string;
    firstName: string;
    lastName: string;
    email: string;
    verificationStatus: string;
  };
  category: {
    id: string;
    name: string;
  };
}

export const adminApi = {
  getStats: () =>
    api.get<ApiResponse<AdminStats>>('/api/admin/stats'),

  getPendingCourses: (params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<CourseForReview[]>>('/api/admin/courses/pending', { params }),

  reviewCourse: (courseId: string, data: {
    action: 'APPROVE' | 'REJECT';
    reviewNotes?: string;
    rejectionReason?: string;
  }) => api.post<ApiResponse<CourseForReview>>(`/api/admin/courses/${courseId}/review`, data),

  getReports: (params?: { status?: string; priority?: string; page?: number; limit?: number }) =>
    api.get<ApiResponse<ContentReport[]>>('/api/admin/reports', { params }),

  resolveReport: (reportId: string, data: {
    resolution: string;
    resolutionType: 'NO_ACTION' | 'WARNING_ISSUED' | 'CONTENT_REMOVED' | 'COURSE_UNPUBLISHED' | 'ACCOUNT_SUSPENDED';
  }) => api.post<ApiResponse<ContentReport>>(`/api/admin/reports/${reportId}/resolve`, data),

  getProfessors: (params?: { page?: number; limit?: number }) =>
    api.get<ApiResponse<unknown[]>>('/api/admin/professors', { params }),

  issueViolation: (data: {
    professorId: string;
    severity: 'CRITICAL' | 'SEVERE' | 'MODERATE';
    type: string;
    description: string;
    evidenceUrls?: string[];
    reportId?: string;
    recordingId?: string;
  }) => api.post<ApiResponse<unknown>>('/api/admin/violations', data),
};

export default api;

